import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from "@mui/icons-material/Delete";
import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, styled, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { Fragment, useCallback, useState } from 'react';
import { ArrayField, Button, Datagrid, DateField, FormDataConsumer, FunctionField, Labeled, NumberField, ReferenceField, required, SimpleForm, SimpleShowLayout, TextField, Toolbar, useCreate, useDataProvider, useGetIdentity, useInput, UseInputValue, useNotify, useRecordContext, useRefresh } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { UserRole } from "../../providers/authProvider";
import { formatCnpj, formatPrice, getStationId, readXmlFile } from '../../utils/utils';
import CnpjField from "../CnpjField";
import UploadFileButton from './UploadFileButton';

interface Invoice {
  invoiceFile: Blob | { rawFile: Blob };
  fileName: string;
  invoiceNumber: string;
  serie: string;
  issueDate: string;
  accessKey: string;
  cnpj: string;
  emitCnpj: string;
  invoiceValue: number;
  totalInvoiceValue: number;
  fillinIds: string[];
  xmlData: any;
  invoiceProducts?: any[];
  invoiceProductIndex: number;
}

type InvoiceFillin = {
  fillinIds: string[];
  invoiceNumber: string;
}

type InvoiceProductIndex = {
  index: number;
  invoiceNumber: string;
}

const useStyles = makeStyles({
  fileNameColumn: {
    width: '200px',
  },
  iconButton: {
    width: '60px',
  }
});

const AccessKeyField: React.FC<{ source: string, label: string, emptyText: string }> = (props) => {
  const record = useRecordContext();
  return (
    <Tooltip title={record.accessKey || ''} placement="bottom">
      <Typography variant="body2">{(record[props.source] || '').slice(0, 15).concat('...')}</Typography>
    </Tooltip>
  );
}

const LinkedFillinField: React.FC<{ invoiceProductIndexes: InvoiceProductIndex[] }> = ({ invoiceProductIndexes }) => {
  const record = useRecordContext();
  const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === record.invoiceNumber)?.index || 0;
  return (
    <FunctionField render={() => `${invoiceProductIndex}/${record.invoiceProducts.length || 1}`} />
  );
}

const UploadInvoiceXml = ({ selectedIds, selectedFillins, setSelectedIds, currentSelectedIds, setCurrentSelectedIds, xmlLoaded, setXmlLoaded, ...props }: any) => {
  const [invoiceFillins, setInvoiceFillins] = useState<InvoiceFillin[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isLoading }] = useCreate();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const classes = useStyles();
  const [validateErrors, setValidateErrors] = useState<any>(null);
  const [validateWarnings, setValidateWarnings] = useState<any>(null);
  const [invoiceProductIndexes, setInvoiceProductIndexes] = useState<InvoiceProductIndex[]>([]);

  const resetInitialValues = (hardReset?: boolean) => {
    setValidateErrors(null);
    setValidateWarnings(null);
    props.resetInitialValues(hardReset);
  }

  const uploadFile = async (props: any, clear: () => void) => {

    const { ...invoicePayload } = props as { invoices: Array<Invoice>, invoiceFiles: Array<{ rawFile: Blob }> };
    const invoiceWithFillin = invoicePayload.invoices.map((invoice: Invoice) => ({ ...invoice, fillinIds: invoiceFillins.find(invoiceF => invoiceF.invoiceNumber === invoice.invoiceNumber)?.fillinIds }))

    if (invoiceWithFillin.some(invoice => invoiceProductIndexes.find(i => i.invoiceNumber === invoice.invoiceNumber)?.index !== invoice.invoiceProducts?.length)) {
      notify('Há uma ou mais notas fiscais sem abastecimentos associados.', { type: 'warning' });
      return null;
    }

    const invoiceFiles = invoicePayload.invoices.map(invoice => invoicePayload.invoiceFiles.find(invoiceFile => invoiceFile.rawFile === invoice.invoiceFile));
    const invoices = invoiceWithFillin.map(invoice => ({
      fillins: (selectedFillins || []).filter(sf => invoice.fillinIds?.includes(sf.id)).map((selectedFillin: any) => ({
        fillinId: selectedFillin.id,
        amount: selectedFillin.amount,
        value: selectedFillin.value,
        ...(selectedFillin.fuelANP ?
          { fuelANP: selectedFillin.fuelANP } :
          { fuelId: selectedFillin.fuelId }
        ),
      })),
      invoiceCnpj: invoice.cnpj,
      emitCnpj: invoice.emitCnpj,
      invoiceNumber: invoice.invoiceNumber,
      serie: invoice.serie,
      issueDate: invoice.issueDate,
      accessKey: invoice.accessKey,
    }));

    create('invoices', { data: { invoiceFiles, invoices } }, {
      onSuccess: (data) => {
        notify('Upload feito com sucesso');

        resetInitialValues(true);
        setInvoiceFillins([]);
        clear();
        refresh();
      },
      onError: (error: any) => {
        if (error.status) {
          notify(error.message || 'Não foi possível realizar o upload', { type: 'warning' });
          return;
        }
      },
    });
  }

  const UploadInvoiceToolbar = ({ handleSubmit, ...props }: { handleSubmit: (value: object, clear: () => void) => void }) => {
    const { getValues, resetField, getFieldState, ...form } = useFormContext();

    const clear = () => {
      resetField("invoiceFiles");
      resetField("invoiceFilePdf");
      resetField("billNumber");
      resetField("billValue");
      resetField("billExpiration");
      resetField("invoiceNumber");
      resetField("destCNPJ");
      resetField("serie");
      resetField("issueDate");
      resetField("accessKey");
      resetField("totalInvoiceValue");
      resetField("subject");
      resetField("body");
      resetField("chatFiles");
      resetField("vehicleBaseName");
      resetField("invoices");
    }

    return (
      <Toolbar {...props} >
        <Button
          startIcon={isLoading ? <CircularProgress size={20} /> : <UploadIcon style={{ fontSize: 20 }} />}
          disabled={isLoading}
          label="Enviar Nota"
          onClick={form.handleSubmit(() => handleSubmit(getValues(), clear))}
          variant="contained"
          size="medium"
        />
      </Toolbar>
    );
  };

  const getVehicleBaseByCnpjApi = async (cnpj: string, vehicleBaseNameInput: UseInputValue) => {
    // @ts-ignore
    const { data } = await dataProvider.getList('vehicle-bases', { filter: { cnpj } });
    if (data && data.length) {
      return data[0];
    } else {
      return null;
    }
  }

  const getFuelApi = async (filter: any) => {
    // @ts-ignore
    const { data } = await dataProvider.getList('fuels', { filter });
    if (data && data.length) {
      return data[0];
    } else {
      return null;
    }
  }

  const handleFileChange = async (files: Array<Blob | { rawFile: Blob }>, invoicesInput: UseInputValue, invoiceFiles: UseInputValue, destCNPJInput: UseInputValue, totalInvoiceValueInput: UseInputValue, vehicleBaseNameInput: UseInputValue) => {
    if (!Array.isArray(files)) { throw null; }

    try {
      let totalInvoiceValue = 0;
      let invoices: Array<Invoice> = [];
      const pendingInvoices = (invoicesInput.field.value || []).filter(({ invoiceNumber }: { invoiceNumber: string }) => !invoiceFillins.find((invoiceF: { invoiceNumber: string }) => invoiceF.invoiceNumber === invoiceNumber));

      if (files.length > (invoicesInput.field.value || []).length && pendingInvoices.length > 0) {
        invoiceFiles.field.onChange(invoiceFiles.field.value); // reset
        notify('Nota fiscal sem abastecimento vinculado, por favor, selecione os abastecimentos listados abaixo.', { type: 'warning', autoHideDuration: 10000 });
        return;
      }

      if ((files.length - invoicesInput.field.value.length) > 1) {
        files = files.slice(0, invoicesInput.field.value.length + 1);
        invoiceFiles.field.onChange(files);

        notify('Limite-se a um arquivo por vez.', { type: 'warning', autoHideDuration: 10000 });
      }

      if (!files || !files.length) {
        resetInitialValues(true);
        setInvoiceFillins([]);
        invoicesInput.field.onChange([]); // reset
        return;
      }

      let error = false;
      let xmlData: { [k: string]: any } = xmlLoaded;

      setXmlLoaded(null);

      let indexFile = 0;

      for (let file of files) {
        indexFile++;
        if (!(file instanceof Blob) && file.rawFile) { file = file.rawFile; }

        const xml = await readXmlFile(file as Blob);
        totalInvoiceValue += parseFloat(xml.getElementsByTagName('vNF')[0].textContent);
        const invoiceSerie = xml.getElementsByTagName('serie')[0];
        const invoiceIssueDate = xml.getElementsByTagName('dhEmi')[0];
        const invoiceAccessKey = xml.getElementsByTagName('chNFe')[0];
        const invoiceNumber = xml.getElementsByTagName('nNF')[0];
        const invoiceCNPJ = xml.getElementsByTagName('dest')[0].getElementsByTagName('CNPJ')[0];
        const emitCNPJ = xml.getElementsByTagName('emit')[0].getElementsByTagName('CNPJ')[0];
        const products = xml.getElementsByTagName('det');
        const productTag = products[0].getElementsByTagName('prod')[0];
        const invoiceAmount = productTag.getElementsByTagName('qCom')[0];
        const totals = xml.getElementsByTagName('total')[0];
        const invoiceValue = parseFloat(totals.getElementsByTagName('vNF')[0].textContent);

        const invoiceProductPromises = [];

        for (const productTag of products) {
          invoiceProductPromises.push(async () => {
            const anpCode = productTag.getElementsByTagName('cProdANP')[0]?.textContent || null;

            const fuel = await getFuelApi(anpCode ? { anpCode: anpCode } : { fuelType: 16 });

            const valueWithDiscount = parseFloat(productTag.getElementsByTagName('vProd')[0]?.textContent || 0) - parseFloat(productTag.getElementsByTagName('vDesc')[0]?.textContent || 0);

            return {
              amount: parseFloat(productTag.getElementsByTagName('qCom')[0]?.textContent || 0),
              value: valueWithDiscount,
              fuelANP: anpCode,
              fuel,
            };
          });
        }

        const invoiceProducts = await Promise.all(invoiceProductPromises.map(f => f()));

        const xmlLoaded: any = {};
        const invoiceNumbers = (invoicesInput.field.value || []).map((item: any) => item.invoiceNumber);
        const invoiceNumberFormated = ("000000000" + invoiceNumber.textContent).slice(-9);

        if (
          invoices &&
          invoices.find((invoice: any) => invoice.invoiceNumber === invoiceNumberFormated) &&
          file === files.slice(-1)[0]
        ) {
          notify('A nota fiscal já foi carregada.', { type: 'warning' });
          invoiceFiles.field.onChange(invoiceFiles.field.value); // reset
          error = true;
          return file;
        }

        const invoiceFillin = (invoicesInput.field.value || []).find((invoice: any) => invoice.invoiceNumber === invoiceNumberFormated);
        if (indexFile === files.length && invoiceFillin) {
          xmlData = invoiceFillin.xmlData;
        }

        const checkInvoiceImported = (invoicesInput.field.value || []).find((invoice: any) => invoice.invoiceNumber === invoiceNumberFormated);
        if (checkInvoiceImported) {
          invoices.push(checkInvoiceImported);
          continue;
        }

        if (invoiceAmount && invoiceAmount.textContent) {
          xmlLoaded.amount = parseFloat(invoiceAmount.textContent || 0);
        }

        if (invoiceValue && invoiceValue) {
          xmlLoaded.value = invoiceValue;
        }

        if (invoiceIssueDate && invoiceIssueDate.textContent) {
          xmlLoaded.issueDate = invoiceIssueDate.textContent;
        }

        const fetchApiPromises = [];

        if (xml && invoiceCNPJ.textContent) {
          xmlLoaded.destCnpj = invoiceCNPJ.textContent;
          fetchApiPromises.push((async () => {
            xmlLoaded.vehicleBase = await getVehicleBaseByCnpjApi(invoiceCNPJ.textContent, vehicleBaseNameInput);
          })());
        }

        xmlLoaded.fuelANP = invoiceProducts[0]?.fuelANP || null;
        xmlLoaded.fuel = invoiceProducts[0]?.fuel;
        xmlLoaded.products = invoiceProducts;

        await Promise.all(fetchApiPromises);

        if (xml && emitCNPJ.textContent) {
          // @ts-ignore
          const stations = await dataProvider.getList('stations', {
            filter: { cnpj: emitCNPJ.textContent },
          });

          if (stations.data.length === 0) {
            notify('Posto não consta em nosso banco de dados.', { type: 'warning' });
            invoiceFiles.field.onChange(invoiceFiles.field.value); // reset
            error = true;
            return file;
          }

          if (identity?.role === UserRole.station && stations.data[0].id !== getStationId()) {
            notify('Posto diverge do cadastrado.', { type: 'warning' });
            invoiceFiles.field.onChange(invoiceFiles.field.value); // reset
            error = true;
            return file;
          }

          xmlLoaded.station = stations.data[0];
        }

        if (!invoiceNumbers.find((number: string) => number === invoiceNumberFormated)) {
          // @ts-ignore
          const invoicesResponse = await dataProvider.getList('invoices', {
            filter: { accessKey: invoiceAccessKey.textContent },
          });
          if (invoicesResponse.data.length > 0) {
            notify('Esta nota fiscal já foi processada e consta em nosso banco de dados.', { type: 'warning' });
            invoiceFiles.field.onChange(invoiceFiles.field.value); // reset
            error = true;
            return file;
          }
        }

        xmlData = { ...xml, ...xmlLoaded };

        invoices.push({
          invoiceFile: file,
          fileName: (file as File).name || 'Não identificado',
          invoiceNumber: invoiceNumberFormated,
          serie: invoiceSerie.textContent,
          issueDate: invoiceIssueDate.textContent,
          accessKey: invoiceAccessKey.textContent,
          cnpj: invoiceCNPJ.textContent,
          emitCnpj: emitCNPJ.textContent,
          totalInvoiceValue: invoiceValue,
          fillinIds: [],
          invoiceValue,
          xmlData,
          invoiceProducts,
          invoiceProductIndex: 0,
        });
      };

      if (!error) {
        resetInitialValues();
      }

      const currentInvoice = invoices[invoices.length - 1];
      if (!currentInvoice) {
        resetInitialValues(true);
      }

      setXmlLoaded((val: object) => ({ ...val, ...xmlData }));

      invoicesInput.field.onChange(invoices);

      // removes all fillins linked to the removed invoice
      //@ts-ignore
      setInvoiceFillins((invoiceFillins: Array<{ invoiceNumber: string }>) =>
        invoiceFillins.filter((invoiceFillin: { invoiceNumber: string }) => invoices.find(invoice => invoice && invoice.invoiceNumber === invoiceFillin.invoiceNumber))
      );

    } catch (error) {
      notify('Falha ao carregar o arquivo XML', { type: 'error' });
      invoiceFiles.field.onChange(invoiceFiles.field.value);
    }
  }

  const invoiceValidation = useCallback((invoiceData: any, fillins: any[]) => {
    const errors: any = {};
    const warnings: any = {};

    if (!invoiceData) { return { errors: null, warnings: null }; }

    if (fillins.some(f => f.invoiceCnpj !== invoiceData.cnpj)) {
      warnings.cnpj = true;
    }

    const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === invoiceData.invoiceNumber)?.index || 0;

    if (invoiceData.invoiceProducts[invoiceProductIndex]?.fuel && fillins.some(f => !invoiceData.invoiceProducts.some(p => p.fuel.id === f.fuelId))) {
      errors.fuel = true;
      errors.fuelMessage = invoiceData.invoiceProducts[invoiceProductIndex].fuel.name;
    }

    if (Math.abs(invoiceData.invoiceProducts[invoiceProductIndex].value - fillins.reduce((acc, cur) => acc += cur.value, 0)) > 20) {
      errors.value = true;
      errors.valueMessage = invoiceData.invoiceProducts[invoiceProductIndex].value;
    }

    if (Math.abs(invoiceData.invoiceProducts[invoiceProductIndex].amount - fillins.reduce((acc, cur) => acc += cur.amount, 0)) > 0.01) {
      errors.amount = true;
      errors.amountMessage = invoiceData.invoiceProducts[invoiceProductIndex].amount;
    }

    return { errors: Object.keys(errors).length ? errors : null, warnings: Object.keys(warnings).length ? warnings : null };
  }, [invoiceProductIndexes]);

  const handleConfirmInvoice = useCallback((invoice: any) => {
    if (!currentSelectedIds.length || !selectedFillins.length) {
      notify('Nenhum abastecimento selecionado', { type: 'warning' });
      return;
    }

    if (!openDialog) {
      const { errors, warnings } = invoiceValidation(invoice, selectedFillins.filter(f => currentSelectedIds.includes(f.id)));

      if (errors || warnings) {
        setValidateErrors(errors);
        setValidateWarnings(warnings);
        setOpenDialog(true);
        return;
      }
    }

    const invoiceFillin = invoiceFillins.find((invoiceFillin: InvoiceFillin) => invoiceFillin.invoiceNumber === invoice.invoiceNumber);
    if (invoiceFillin) {
      invoiceFillin.fillinIds = invoiceFillin.fillinIds.concat(currentSelectedIds);
      setInvoiceFillins(invoiceFillins);
    } else {
      setInvoiceFillins((value: any) => ([...value, { invoiceNumber: invoice.invoiceNumber, fillinId: currentSelectedIds, fillinIds: currentSelectedIds }]));
    }

    const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === invoice.invoiceNumber);
    if (invoiceProductIndex) {
      invoiceProductIndex.index++;
      setInvoiceProductIndexes(invoiceProductIndexes);
    } else {
      setInvoiceProductIndexes(value => ([...value, { invoiceNumber: invoice.invoiceNumber, index: 1 }]));
    }

    if (invoice.invoiceProducts && invoice.invoiceProducts[(invoiceProductIndex?.index || 0) + 1]) {
      setXmlLoaded((value: any) => ({
        ...invoice.xmlData,
        amount: invoice.invoiceProducts[(invoiceProductIndex?.index || 0) + 1].amount,
        value: invoice.invoiceProducts[(invoiceProductIndex?.index || 0) + 1].value,
        fuel: invoice.invoiceProducts[(invoiceProductIndex?.index || 0) + 1].fuel,
      }));
    }

    setSelectedIds((ids: string[]) => ([...ids, ...currentSelectedIds]));
    setCurrentSelectedIds([]);
    setOpenDialog(false);
  }, [invoiceProductIndexes, invoiceFillins, openDialog, selectedFillins, currentSelectedIds]);

  const DialogConfirm = () => {
    const record = useRecordContext();
    if (!record) { return null; }
    const closeDialog = () => {
      setOpenDialog(false);
    }

    const currentFillin = selectedFillins.find((fillin: any) => fillin.id === currentSelectedIds[0]);
    const currentSelectedFillins = selectedFillins.filter(f => currentSelectedIds.includes(f.id)) || [];

    return (
      <Fragment>
        <ReadyButton headerClassName={classes.iconButton} />
        <Dialog
          fullWidth
          open={openDialog}
        >
          <DialogTitle textAlign="center">Divergência</DialogTitle>
          <DialogContent>
            <Stack gap={1}>
              <Box component="fieldset" sx={{ border: '1px dashed grey' }}>
                <Legend>Detalhes do(s) abastecimento(s)</Legend>
                <SimpleShowLayout record={{ ...currentFillin, value: currentSelectedFillins.reduce((acc, cur) => acc += cur.value, 0), amount: currentSelectedFillins.reduce((acc, cur) => acc += cur.amount, 0) }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <SimpleShowLayout sx={{ padding: 0 }}>
                        <ReferenceField source="fuelId" reference="fuels" label="Combustível">
                          <TextField source="name" />
                        </ReferenceField>
                        <CnpjField source="invoiceCnpj" label="CNPJ da nota" isHidden={false} />
                      </SimpleShowLayout>
                    </Grid>
                    <Grid item xs={6}>
                      <SimpleShowLayout sx={{ padding: 0 }}>
                        <NumberField
                          source="value"
                          label="Valor total"
                          options={{ style: 'currency', currency: 'BRL' }}
                        />
                        <NumberField
                          source="amount"
                          label="Litros"
                          options={{ style: 'unit', unit: 'liter' }}
                        />
                      </SimpleShowLayout>
                    </Grid>
                  </Grid>
                </SimpleShowLayout>
              </Box>
              {
                (validateWarnings && validateWarnings.cnpj && record) &&
                <Alert variant="outlined" severity="warning">
                  O CNPJ da nota fiscal está diferente do CNPJ que a transportadora esperava {formatCnpj(record.cnpj)}
                </Alert>
              }

              {
                (validateErrors && validateErrors.fuel && validateErrors.fuelMessage) &&
                <Alert variant="outlined" severity="error">
                  Combustível do abastecimento é diferente da nota: {validateErrors.fuelMessage}
                </Alert>
              }

              {
                (validateErrors && validateErrors.amount && record.xmlData) &&
                <Alert variant="outlined" severity="error">
                  Quantidade em litros diferente do abastecimento: {(new Intl.NumberFormat('pt-BR', { style: 'unit', unit: 'liter' })).format(validateErrors.amountMessage)}
                </Alert>
              }

              {
                (validateErrors && validateErrors.value && record.xmlData) &&
                <Alert variant="outlined" severity="error">
                  Valor total diferente do abastecimento: {formatPrice(validateErrors.valueMessage)}
                </Alert>
              }
            </Stack>
            <DialogActions sx={{ marginTop: 4, padding: 0 }}>
              <Button variant="outlined" onClick={closeDialog} label="ra.action.cancel" />
              <Button variant="contained" onClick={() => handleConfirmInvoice(record)} label="ra.action.confirm" disabled={validateErrors && validateErrors.fuel} />
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Fragment>
    );
  }

  const ReadyButton: React.FC<any> = () => {
    const record = useRecordContext();
    const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === record.invoiceNumber)?.index || 0;
    const checked = !!(invoiceFillins || []).find((invoice: any) => invoice.invoiceNumber === record.invoiceNumber && invoiceProductIndex === record.invoiceProducts?.length);

    return (
      <Button
        sx={{ minWidth: 'auto' }}
        variant="outlined"
        onClick={() => handleConfirmInvoice(record)}
        startIcon={<CheckIcon style={{ marginLeft: 10 }} />}
        disabled={checked}
      />
    );
  }

  const RemoveButton: React.FC<any> = () => {
    const record = useRecordContext();
    const invoiceFiles = useInput({ source: 'invoiceFiles' });
    const invoices = useInput({ source: 'invoices' });
    const invoicesInput = useInput({ source: 'invoices' });
    const destCNPJInput = useInput({ source: 'destCNPJ' });
    const totalInvoiceValueInput = useInput({ source: 'totalInvoiceValue' });
    const vehicleBaseNameInput = useInput({ source: 'vehicleBaseName' });

    const handleRemove = (record: any) => {
      const indexRemoved = invoices.field.value?.findIndex((invoice: any) => invoice.invoiceNumber === record.invoiceNumber);
      const invoiceFillin = invoiceFillins.find(invoiceFillin => invoiceFillin.invoiceNumber === record.invoiceNumber);

      if (invoiceFillin) {
        setSelectedIds((ids: string[]) => ids.filter(id => !invoiceFillin.fillinIds.includes(id)));
      }

      const files = invoiceFiles.field.value.filter((item: any, index: number) => index !== indexRemoved);
      invoiceFiles.field.onChange(invoiceFiles.field.value.filter((item: any, index: number) => index !== indexRemoved));

      handleFileChange(files, invoicesInput, invoiceFiles, destCNPJInput, totalInvoiceValueInput, vehicleBaseNameInput);
    }

    return (
      <Button
        sx={{ minWidth: 'auto' }}
        variant="outlined"
        onClick={() => handleRemove(record)}
        startIcon={<RemoveIcon style={{ marginLeft: 10 }} />}
        color="warning"
      />
    );
  }

  const invoiceStyle = (record: any) => {
    const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === record.invoiceNumber)?.index || 0;
    const isCompleted = (invoiceFillins || []).find((invoice: any) => invoice.invoiceNumber === record.invoiceNumber && invoiceProductIndex === record.invoiceProducts.length);
    const inProgress = (invoiceFillins || []).find((invoice: any) => invoice.invoiceNumber === record.invoiceNumber && invoice.fillinIds.length > 0);
    return {
      backgroundColor: isCompleted ? '#caffdb' : inProgress ? '#FFF59D' : '#FFEBE6',
    };
  };

  return (
    <Card sx={{ marginTop: 2 }}>
      <Box>
        <SimpleForm toolbar={<UploadInvoiceToolbar handleSubmit={uploadFile} />} sanitizeEmptyValues>
          <Grid container spacing={2} columns={12}>
            <Grid item sm={12} md={12}>
              <Box component="div" sx={{ p: 2, border: '1px dashed grey', height: '100%', padding: 0 }}>
                <FormDataConsumer>
                  {({ formData }) => {
                    if (!formData.invoiceFiles || formData.invoiceFiles.length === 0) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 2 }}>
                          <Typography variant="subtitle1" align="center">Aguardando o arquivo XML</Typography>
                        </Box>
                      );
                    }

                    return (
                      <>
                        <Grid container sm={12}>
                          <Grid item md={12}>
                            <SimpleShowLayout record={formData as any}>
                              <ArrayField source="invoices" label="Notas Fiscais" emptyText="Não identificado">
                                <Datagrid
                                  bulkActionButtons={false}
                                  rowSx={invoiceStyle}
                                  sx={{
                                    '& .MuiTypography-root, .MuiTableCell-head': { fontSize: 12 },
                                  }}
                                >
                                  <TextField source="fileName" label="Nome" headerClassName={classes.fileNameColumn} />
                                  <TextField source="invoiceNumber" label="Número" />
                                  <TextField source="invoiceProductIndex" label="invoiceProductIndex" sx={{ display: 'none' }} />
                                  <FunctionField label="CNPJ" render={(record: any) => record.cnpj ? formatCnpj(record.cnpj) : 'Não identificado'} />
                                  <DateField source="issueDate" locales="pt-BR" label="Data de emissão" emptyText="Não identificado" showTime />
                                  <AccessKeyField source="accessKey" label="Chave de acesso" emptyText="Não identificado" />
                                  <FunctionField render={(record: any) => {
                                    const invoiceProductIndex = invoiceProductIndexes.find(i => i.invoiceNumber === record.invoiceNumber)?.index || 0;
                                    return record && record.invoiceProducts && record.invoiceProducts[invoiceProductIndex] ? formatPrice(record.invoiceProducts[invoiceProductIndex].value) : '--';
                                  }} label="Valor do item" />
                                  <LinkedFillinField invoiceProductIndexes={invoiceProductIndexes} />
                                  <RemoveButton headerClassName={classes.iconButton} />
                                  <DialogConfirm />
                                </Datagrid>
                              </ArrayField>
                            </SimpleShowLayout>
                          </Grid>
                        </Grid>
                      </>
                    );
                  }}
                </FormDataConsumer>
              </Box>
            </Grid>

            <Grid item sm={12} md={12}>
              <UploadFileButton
                source="invoiceFiles"
                resetInitialValues={resetInitialValues}
                invoiceFillins={invoiceFillins}
                setInvoiceFillins={setInvoiceFillins}
                invoiceProductIndexes={invoiceProductIndexes}
                //@ts-ignore
                onChange={handleFileChange}
                validate={[required('Campo obrigatório')]}
              />
            </Grid>

            <Grid item sm={12} md={12}>
              <Divider sx={{ marginBottom: 2 }} />
              <Labeled label="Selecione o(s) abastecimento(s)" fullWidth>
                {props.children}
              </Labeled>
            </Grid>

          </Grid>
        </SimpleForm>
      </Box>
    </Card>
  );
};

const Legend = styled('legend')({
  paddingLeft: 12,
  paddingRight: 12,
});

export default React.memo(UploadInvoiceXml);